/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * One cut-over request, in the Change control page's drawer: where it stands, its window, its
 * timeline (requested, decisions with their comments, scheduled, started, the end) and the
 * Approve / Reject / Cancel actions. The four-eyes rule hides Approve and Reject from the
 * requester; the API's own refusal (403, 409) is shown above the buttons.
 */

import * as React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import {
  ArrowRight,
  Ban,
  Check,
  ClipboardCheck,
  LoaderCircle,
  ShieldAlert,
  X,
} from "lucide-react";

import { ActionButton, KeyValueGrid } from "@src/components/ui/DetailsKit";
import { Mono, Pill, StatePill, T9, formatDateTime } from "@src/components/ui/Lists/ListTable";
import {
  FootNote,
  FootSpacer,
  FormField,
  Notice,
  OpsDrawer,
  OpsTextarea,
  SectionHeading,
} from "@src/components/ui/OpsDialog";
import cutoverSource, { apiErrorMessage, apiErrorStatus } from "@src/sources/CutoverSource";
import notificationStore from "@src/stores/NotificationStore";

import type { CutoverAction, CutoverRequest } from "@src/@types/Cutover";

import {
  buildTimeline,
  canCancel,
  decisionState,
  formatDuration,
  formatUtc,
  formatWhen,
  formatWindow,
  instancesLabel,
  isOwnRequest,
  relative,
  requesterName,
  statusInfo,
  windowPhase,
} from "./cutoverUtils";
import { parseApiDate } from "@src/components/ui/Lists/ListTable";

const Section = styled.div`
  margin-top: 22px;
`;
const Timeline = styled.ol`
  list-style: none;
  margin: 10px 0 0 0;
  padding: 0;
`;
const DOT: { [k: string]: string } = {
  brand: T9.brand500,
  success: "var(--t9-success-700)",
  danger: "var(--t9-danger-700)",
  warning: "var(--t9-warning-700)",
  neutral: T9.slate400,
  info: "var(--t9-info-700)",
};
const Event = styled.li<{ tone: string; pending?: boolean }>`
  position: relative;
  padding: 0 0 16px 26px;
  &:before {
    content: "";
    position: absolute;
    left: 5px;
    top: 16px;
    bottom: 0;
    width: 2px;
    background: ${T9.slate200};
  }
  &:last-child:before {
    display: none;
  }
  .dot {
    position: absolute;
    left: 0;
    top: 4px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    box-sizing: border-box;
    background: ${props => (props.pending ? "var(--t9-bg-surface)" : DOT[props.tone])};
    border: 2px solid ${props => DOT[props.tone]};
    ${props => (props.pending ? "border-style: dashed;" : "")}
  }
  .t {
    font-size: 13px;
    line-height: 20px;
    font-weight: 600;
    color: ${props => (props.pending ? T9.slate500 : T9.slate900)};
  }
  .m {
    font-size: 12px;
    line-height: 16px;
    color: ${T9.slate500};
  }
  .c {
    margin-top: 6px;
    padding: 8px 10px;
    border-radius: 6px;
    background: ${T9.slate50};
    border: 1px solid ${T9.slate100};
    font-size: 13px;
    line-height: 18px;
    color: ${T9.slate700};
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;
const HeadPills = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

type Props = {
  request: CutoverRequest;
  transferName?: string | null;
  transferInstances?: string[];
  deploymentStatus?: string | null;
  user: { id?: string; name?: string; isAdmin?: boolean | null } | null | undefined;
  onClose: () => void;
  onChanged: (request: CutoverRequest) => void;
  now?: number;
};

const bannerFor = (r: CutoverRequest, own: boolean, now: number) => {
  const phase = windowPhase(r, now);
  switch (r.status) {
    case "pending_approval":
      return own
        ? { tone: "warning" as const, title: "You can't approve your own request", text: "Someone else must approve or reject it (four-eyes rule). You can still cancel it." }
        : {
            tone: "warning" as const,
            title: "Waiting for approval",
            text:
              phase === "open"
                ? "The window is already open: approving starts the deployment at once."
                : `If approved, SwiftIQ starts the deployment when the window opens (${relative(r.window_start, now)}).`,
          };
    case "approved":
    case "scheduled":
      return {
        tone: "info" as const,
        title: `Scheduled: SwiftIQ deploys ${phase === "before" ? relative(r.window_start, now) : "now"}`,
        text: `The deployment starts at ${formatWhen(r.window_start, true)} and must start before ${formatWhen(r.window_end)}.`,
      };
    case "started":
      return { tone: "info" as const, title: "Deploying", text: "SwiftIQ created the deployment inside the window. Open it to follow the tasks." };
    case "completed":
      return { tone: "success" as const, title: "Cut-over completed", text: "The deployment completed. It can be rolled back from its page while its rollback window lasts." };
    case "failed":
      return { tone: "danger" as const, title: "Cut-over failed", text: r.status_reason || "The deployment failed or could not be created." };
    case "rejected":
      return { tone: "danger" as const, title: "Rejected", text: r.status_reason || "The approver rejected this cut-over." };
    case "cancelled":
      return { tone: "neutral" as const, title: "Cancelled", text: r.status_reason || "Cancelled before it started." };
    case "expired":
      return { tone: "neutral" as const, title: "Expired", text: "The window closed before the request was approved or started." };
    default:
      return null;
  }
};

const CutoverDetails = (props: Props) => {
  const r = props.request;
  const now = props.now || Date.now();
  const [comment, setComment] = React.useState("");
  const [busy, setBusy] = React.useState<CutoverAction | null>(null);
  const [error, setError] = React.useState<{ status: number | null; message: string } | null>(null);
  const [commentError, setCommentError] = React.useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = React.useState(false);

  React.useEffect(() => {
    setComment("");
    setError(null);
    setCommentError(null);
    setConfirmCancel(false);
  }, [r.id]);

  const own = isOwnRequest(r, props.user);
  const decision = decisionState(r, props.user);
  const cancellable = canCancel(r, props.user);
  const info = statusInfo(r.status);
  const banner = bannerFor(r, own, now);
  const events = buildTimeline(r, now);
  const start = parseApiDate(r.window_start);
  const end = parseApiDate(r.window_end);
  const opts = r.deployment_options || {};

  const act = async (action: CutoverAction) => {
    if (action === "reject" && !comment.trim()) {
      setCommentError("Say why you are rejecting it: the requester sees this comment.");
      return;
    }
    setBusy(action);
    setError(null);
    setCommentError(null);
    try {
      const updated = await cutoverSource.action(r.id, action, comment);
      const words: { [a: string]: string } = {
        approve: updated.status === "started" ? "Approved: the window is open, so SwiftIQ started the deployment." : "Approved: SwiftIQ deploys when the window opens.",
        reject: "Cut-over request rejected.",
        cancel: "Cut-over request cancelled.",
      };
      notificationStore.alert(words[action], "success");
      setComment("");
      setConfirmCancel(false);
      props.onChanged(updated);
    } catch (err: any) {
      setError({ status: apiErrorStatus(err), message: apiErrorMessage(err, `Could not ${action} the request.`) });
      // A 409 (wrong state, window ended) means the request moved on: show its current state.
      if (apiErrorStatus(err) === 409) {
        try {
          props.onChanged(await cutoverSource.get(r.id, { skipLog: true }));
        } catch (e) {
          // keep the old copy
        }
      }
    } finally {
      setBusy(null);
    }
  };

  const optionText = [
    `Clone disks: ${opts.clone_disks === false ? "no" : "yes"}`,
    opts.force ? "Force: yes" : null,
    `Skip OS morphing: ${opts.skip_os_morphing ? "yes" : "no"}`,
    opts.user_scripts ? "User scripts: yes" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const footer = (
    <>
      {r.status === "pending_approval" && !decision.canDecide ? (
        <FootNote data-test-id="cutover-own-note">
          <ShieldAlert size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
          {own ? "You can't approve your own request." : decision.reason}
        </FootNote>
      ) : (
        <FootSpacer />
      )}
      {cancellable ? (
        confirmCancel ? (
          <>
            <ActionButton type="button" variant="secondary" onClick={() => setConfirmCancel(false)} disabled={Boolean(busy)}>
              Keep it
            </ActionButton>
            <ActionButton type="button" variant="danger" onClick={() => act("cancel")} disabled={Boolean(busy)} data-test-id="cutover-cancel-confirm">
              {busy === "cancel" ? <LoaderCircle size={16} className="spin" /> : <Ban size={16} />}
              Cancel the request
            </ActionButton>
          </>
        ) : (
          <ActionButton type="button" variant="secondary" onClick={() => setConfirmCancel(true)} disabled={Boolean(busy)} data-test-id="cutover-cancel">
            <Ban size={16} />
            Cancel request
          </ActionButton>
        )
      ) : null}
      {decision.canDecide && !confirmCancel ? (
        <>
          <ActionButton type="button" variant="danger" onClick={() => act("reject")} disabled={Boolean(busy)} data-test-id="cutover-reject">
            {busy === "reject" ? <LoaderCircle size={16} className="spin" /> : <X size={16} />}
            Reject
          </ActionButton>
          <ActionButton type="button" onClick={() => act("approve")} disabled={Boolean(busy)} data-test-id="cutover-approve">
            {busy === "approve" ? <LoaderCircle size={16} className="spin" /> : <Check size={16} />}
            Approve
          </ActionButton>
        </>
      ) : null}
    </>
  );

  return (
    <OpsDrawer
      title={`Cut-over of ${props.transferName || "transfer"}`}
      subtitle={
        <HeadPills>
          <Pill tone={info.tone} icon={info.icon} spin={info.spin}>
            {info.label}
          </Pill>
          <Mono style={{ color: T9.slate500 }}>{r.id}</Mono>
        </HeadPills>
      }
      icon={ClipboardCheck}
      width={620}
      onClose={props.onClose}
      footer={footer}
      testId="cutoverDetails"
    >
      {banner ? (
        <Notice tone={banner.tone} title={banner.title}>
          {banner.text}
        </Notice>
      ) : null}
      {error ? (
        <Notice
          tone="danger"
          title={error.status === 403 ? "Not allowed" : error.status === 409 ? "The request has moved on" : "SwiftIQ refused this"}
          style={{ marginTop: 12 }}
          testId="cutover-error"
        >
          {error.message}
        </Notice>
      ) : null}

      <Section style={{ marginTop: 18 }}>
        <KeyValueGrid
          items={[
            {
              label: "Transfer",
              value: (
                <Link to={`/transfers/${r.transfer_id}`} style={{ color: T9.brand600, textDecoration: "none", fontWeight: 500 }}>
                  {props.transferName || r.transfer_id}
                </Link>
              ),
            },
            { label: "VMs", value: instancesLabel(r, props.transferInstances) },
            { label: "Change window", value: formatWindow(r.window_start, r.window_end), span: 2 },
            {
              label: "In UTC",
              value: `${formatUtc(r.window_start)} – ${formatUtc(r.window_end)}${start && end ? ` (${formatDuration(end.getTime() - start.getTime())})` : ""}`,
              span: 2,
            },
            { label: "Reason / change reference", value: r.reason || null, span: 2 },
            { label: "Requested by", value: `${requesterName(r)}${own ? " (you)" : ""}` },
            { label: "Requested", value: `${formatDateTime(r.created_at)} · ${relative(r.created_at, now)}` },
            {
              label: "Deployment",
              value: r.deployment_id ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Link to={`/deployments/${r.deployment_id}`} style={{ color: T9.brand600, textDecoration: "none", fontWeight: 500 }}>
                    Open deployment <ArrowRight size={13} style={{ verticalAlign: -2 }} />
                  </Link>
                  {props.deploymentStatus ? <StatePill status={props.deploymentStatus} /> : null}
                </span>
              ) : null,
            },
            {
              label: "Rollback window",
              value:
                opts.rollback_retention_hours === 0
                  ? "Off"
                  : opts.rollback_retention_hours
                    ? `${opts.rollback_retention_hours} h after it completes`
                    : "SwiftIQ's default (72 h)",
            },
            { label: "Deployment options", value: optionText, span: 2 },
          ]}
        />
      </Section>

      <Section>
        <SectionHeading>Timeline</SectionHeading>
        <Timeline data-test-id="cutover-timeline">
          {events.map(e => (
            <Event key={e.key} tone={e.tone} pending={e.pending}>
              <span className="dot" />
              <div className="t">{e.title}</div>
              <div className="m">
                {[e.who, e.at ? `${formatDateTime(e.at)} · ${relative(e.at, now)}` : null].filter(Boolean).join(" · ")}
              </div>
              {e.comment ? <div className="c">{e.comment}</div> : null}
            </Event>
          ))}
        </Timeline>
      </Section>

      {decision.canDecide || cancellable ? (
        <Section>
          <FormField
            label={decision.canDecide ? "Comment" : "Comment (optional)"}
            error={commentError}
            hint={decision.canDecide ? "Optional when approving, required when rejecting. Kept in the audit history." : "Kept with the cancellation."}
          >
            <OpsTextarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={decision.canDecide ? "OK for Saturday's window" : "Moved to next week"}
              invalid={Boolean(commentError)}
              data-test-id="cutover-comment"
            />
          </FormField>
        </Section>
      ) : null}
    </OpsDrawer>
  );
};

export default CutoverDetails;
